/**
 * Manufacturer → Revision terminology (shared-types v1.142.0): the maker's own
 * word for one revision and its plural — Seiko "Revision Code", ETA
 * "Revision", others "Generation". A preset pair or custom text; blank means
 * the default "Revision" / "Revisions". Saved with the form.
 */
import { useState } from 'react';
import { DEFAULT_REVISION_TERMS, REVISION_TERM_PRESETS, presetIndex } from '../../services/revisionTerms';

interface Props {
  singular: string;
  plural: string;
  onChange: (singular: string, plural: string) => void;
}

const CUSTOM = 'custom';

export function RevisionTermsField({ singular, plural, onChange }: Props) {
  const blank = !singular.trim() && !plural.trim();
  const idx = blank ? 0 : presetIndex({ singular: singular.trim(), plural: plural.trim() });
  // Custom stays chosen while its inputs are still blank or happen to match a preset.
  const [custom, setCustom] = useState(idx < 0);
  const value = custom || idx < 0 ? CUSTOM : String(idx);
  const inputClass = 'mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm';

  return (
    <div className="space-y-2 rounded-md border border-gray-200 p-3">
      <label className="block text-sm">
        <span className="font-medium text-gray-700">Revision terminology</span>
        <span className="block text-xs text-gray-500">
          What this maker calls one revision of a calibre. Used in the AI&apos;s revision-line suggestions, the
          admin&apos;s revision-line copy, and later the app.
        </span>
        <select
          aria-label="Revision terminology"
          value={value}
          onChange={(e) => {
            if (e.target.value === CUSTOM) return setCustom(true);
            setCustom(false);
            const p = REVISION_TERM_PRESETS[Number(e.target.value)];
            // The default pair is stored as absent.
            if (p === DEFAULT_REVISION_TERMS) onChange('', '');
            else onChange(p.singular, p.plural);
          }}
          className={inputClass}
        >
          {REVISION_TERM_PRESETS.map((p, i) => (
            <option key={p.singular} value={String(i)}>
              {p.singular} / {p.plural}
              {i === 0 ? ' (default)' : ''}
            </option>
          ))}
          <option value={CUSTOM}>Custom…</option>
        </select>
      </label>
      {value === CUSTOM && (
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-xs text-gray-600">
            Singular
            <input
              aria-label="Revision term (singular)"
              value={singular}
              placeholder="Revision"
              onChange={(e) => onChange(e.target.value, plural)}
              className={inputClass}
            />
          </label>
          <label className="block text-xs text-gray-600">
            Plural
            <input
              aria-label="Revision term (plural)"
              value={plural}
              placeholder="Revisions"
              onChange={(e) => onChange(singular, e.target.value)}
              className={inputClass}
            />
          </label>
        </div>
      )}
    </div>
  );
}
